import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../images/lavenir-logo.png';
import axios from 'axios';
import { toast } from 'react-toastify';

const EnterprisePurchaseNew = ({ onBack }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    seats: 10,
    name: '',
    email: '',
    companyName: '',
    region: 'US',
    otherInfo: '',
    acceptEmailResponse: false
  });

  // Check if form is valid using useMemo to prevent unnecessary recalculations
  const isFormValid = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return formData.name.trim() !== '' && 
           emailRegex.test(formData.email) && 
           formData.acceptEmailResponse;
  }, [formData.name, formData.email, formData.acceptEmailResponse]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'seats') {
      const numValue = parseInt(value);
      if (numValue > parseInt(process.env.REACT_APP_ENTERPRISE_MAX_SEATS)) {
        alert(`Enterprise plan is limited to ${process.env.REACT_APP_ENTERPRISE_MAX_SEATS} seats. Please contact us directly for larger deployments.`);
        setFormData(prev => ({ ...prev, seats: parseInt(process.env.REACT_APP_ENTERPRISE_MAX_SEATS) }));
        return;
      }
      if (numValue < parseInt(process.env.REACT_APP_ENTERPRISE_MIN_SEATS)) {
        alert(`Minimum number of seats is ${process.env.REACT_APP_ENTERPRISE_MIN_SEATS}.`);
        setFormData(prev => ({ ...prev, seats: parseInt(process.env.REACT_APP_ENTERPRISE_MIN_SEATS) }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        seats: value === '' ? '' : numValue
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const createEnterpriseInquiryEmail = (formData) => {
    return {
      to: process.env.REACT_APP_ENTERPRISE_EMAIL_TO,

      // to: 'jcrist70@gmail.com',

      from: process.env.REACT_APP_EMAIL_FROM,
      replyTo: formData.email,
      subject: `ENTERPRISE INQUIRY: ${formData.companyName || 'New Organization'}`,
      text: `Enterprise Inquiry Details:
        - Name: ${formData.name}
        - Email: ${formData.email}
        - Organization: ${formData.companyName || 'Not provided'}
        - Region: ${formData.region}
        - Estimated Seats: ${formData.seats}
        - Additional Information: ${formData.otherInfo || 'None provided'}
        
        Please follow up accordingly.`,
      html: `
        <h2>Enterprise Inquiry Details</h2>
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Organization:</strong> ${formData.companyName || 'Not provided'}</p>
        <p><strong>Region:</strong> ${formData.region}</p>
        <p><strong>Estimated Seats:</strong> ${formData.seats}</p>
        <p><strong>Additional Information:</strong> ${formData.otherInfo || 'None provided'}</p>
        <br>
        <p>Please follow up accordingly.</p>
      `
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const emailData = createEnterpriseInquiryEmail(formData);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API}/enterprise-inquiry`,
        emailData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        toast.success('Enterprise inquiry sent successfully');
        navigate('/login');
      }
      
    } catch (error) {
      console.error('Error sending enterprise inquiry:', error);
      toast.error(error.message || 'Failed to send inquiry. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-[#eef2f3] via-[#d9e6f2] to-[#eef2f3] py-6">
      <div className="max-w-[540px] mx-auto">
        <div className="bg-white rounded-[12px] shadow-lg p-7">
          <img src={Logo} alt="LavenirAI" className="h-[42px] mx-auto mb-4" />
          
          <h1 className="text-[#10193A] text-[28px] font-bold text-center mb-6">
            Contact LavenirAI for Enterprise
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-600 mb-1">Number of Seats (Estimated):</label>
              <input
                type="number"
                name="seats"
                min={process.env.REACT_APP_ENTERPRISE_MIN_SEATS}
                max={process.env.REACT_APP_ENTERPRISE_MAX_SEATS}
                value={formData.seats}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Name:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Company Name:</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Main Region:</label>
              <select
                name="region"
                value={formData.region}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
                required
              >
                <option value="US">US</option>
                <option value="EU">EU</option>
                <option value="Asia">Asia</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Other Info (optional):</label>
              <textarea
                name="otherInfo"
                value={formData.otherInfo}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg h-24"
              />
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="acceptEmail"
                name="acceptEmailResponse"
                checked={formData.acceptEmailResponse}
                onChange={handleChange}
                className="mt-1"
              />
              <label htmlFor="acceptEmail" className="text-sm text-gray-600">
                I accept an email response from LavenirAI's team.
              </label>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onBack}
                className="w-1/4 py-2.5 border border-[#10193A] text-[#10193A] rounded-xl"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`w-3/4 rounded-xl py-2.5 ${
                  isFormValid 
                    ? 'bg-[#10193A] text-white' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Contact Us
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnterprisePurchaseNew;